import React, { useState } from 'react';
import styled from '@emotion/styled';
import { TitleLarge, SubtitleMedium } from '../components/Typography';
import Button from '../components/Button';
import { colors } from '../theme/tokens';
import logoHorizontal from '../assets/logo-horizontal.png';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const ForgotPasswordContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: ${colors.neutral.offWhite};
`;

const ForgotPasswordForm = styled.div`
  background: ${colors.neutral.white};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const Logo = styled.img`
  width: 200px;
  height: auto;
  margin-bottom: 2rem;
`;

const TitleSection = styled.div`
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  text-align: left;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${colors.neutral.darkCharcoal};
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${colors.neutral.lightCharcoal};
  border-radius: 4px;
  font-size: 1rem;
  background-color: ${colors.neutral.white};
  color: ${colors.neutral.black};
  
  &:focus {
    outline: none;
    border-color: ${colors.brand.laurel};
    background-color: ${colors.neutral.offWhite};
  }
`;

const ErrorMessage = styled.div`
  color: ${colors.reds.bright};
  margin-top: 0.5rem;
  font-size: 0.875rem;
`;

const SuccessMessage = styled.div`
  color: ${colors.brand.laurel};
  margin-top: 0.5rem;
  font-size: 0.875rem;
`;

const BackToLogin = styled.div`
  margin-top: 1rem;
  font-size: 0.875rem;
  color: ${colors.neutral.darkCharcoal};
  
  a {
    color: ${colors.brand.laurel};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ForgotPassword: React.FC = () => {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'An error occurred while resetting your password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ForgotPasswordContainer>
      <ForgotPasswordForm>
        <Logo src={logoHorizontal} alt="Appetite Admin" />
        <TitleSection>
          <TitleLarge>Forgot Password</TitleLarge>
          <SubtitleMedium>Enter your email to reset your password</SubtitleMedium>
        </TitleSection>
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={success}
            />
          </FormGroup>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && (
            <SuccessMessage>
              Check your email for a password reset link
            </SuccessMessage>
          )}
          <Button type="submit" disabled={isLoading || success}>
            {isLoading ? 'Sending...' : 'Send Reset Link'}
          </Button>
          <BackToLogin>
            <a href="/login">Back to Login</a>
          </BackToLogin>
        </form>
      </ForgotPasswordForm>
    </ForgotPasswordContainer>
  );
};

export default ForgotPassword; 